import { articleSlugFromPath } from '../../shared/article-types.js';
import { articleBySlug } from '../../shared/articles.js';
import { DEFAULT_LOCALE, type Locale } from '../../shared/locales.js';
import { NOT_FOUND_PATH, normalisePath } from '../../shared/routes.js';
import { LocaleContext } from '../i18n.js';
import { Layout } from './Layout.js';
import { ArticlePage } from './pages/ArticlePage.js';
import { Blog } from './pages/Blog.js';
import { Faq } from './pages/Faq.js';
import { Guides } from './pages/Guides.js';
import { Home } from './pages/Home.js';
import { HowItWorks } from './pages/HowItWorks.js';
import { NotFound } from './pages/NotFound.js';
import { Privacy } from './pages/Privacy.js';
import { BacheloretteBudget, GroupGiftBudget, GroupTripBudget } from './pages/UseCase.js';

/**
 * The public site.
 *
 * There is no client-side router. Every public page is prerendered to real HTML at build
 * time, in every language, and links are ordinary anchors, so navigation is a normal page
 * load: no route table shipped to the browser, no hydration mismatch, no blank frame while
 * a chunk downloads, and correct behaviour with JavaScript disabled.
 *
 * `path` is always the route without its language prefix. `/es/faq` and `/faq` are the
 * same page in two languages, so they render the same component and differ only in the
 * dictionary the context hands them.
 */

const PAGES: Record<string, () => React.JSX.Element> = {
  '/': Home,
  '/how-it-works': HowItWorks,
  '/privacy': Privacy,
  '/faq': Faq,
  '/guides': Guides,
  '/blog': Blog,
  '/group-trip-budget': GroupTripBudget,
  '/bachelorette-budget': BacheloretteBudget,
  '/group-gift-budget': GroupGiftBudget,
  [NOT_FOUND_PATH]: NotFound,
};

interface PublicAppProps {
  path: string;
  locale?: Locale;
}

export function PublicApp({ path, locale = DEFAULT_LOCALE }: PublicAppProps) {
  const normalised = normalisePath(path);

  return (
    <LocaleContext.Provider value={locale}>
      <Layout path={normalised}>{renderPage(normalised, locale)}</Layout>
    </LocaleContext.Provider>
  );
}

function renderPage(path: string, locale: Locale): React.JSX.Element {
  // Articles are matched before the fixed routes: their paths are data, not a table.
  const slug = articleSlugFromPath(path);
  if (slug !== null) {
    const article = articleBySlug(locale, slug);
    return article ? <ArticlePage article={article} /> : <NotFound />;
  }

  const Page = PAGES[path] ?? NotFound;
  return <Page />;
}
